import event_utility_helpers from "./event_utility_helpers";

function sold_ticket_count(event) {
  if (!Array.isArray(event?.event_tickets)) {
    return Number(event?.ticket_purchase_count || 0);
  }

  let total = 0;
  for (const ticket of event.event_tickets) {
    if (ticket?.sold_instances !== undefined && ticket?.sold_instances !== null) {
      total += Number(ticket.sold_instances || 0);
      continue;
    }
    const quantity = Number(ticket?.quantity || 0);
    const available = Number(ticket?.available_instances || 0);
    total += Math.max(0, quantity - available);
  }
  return total;
}

function total_registrations(event) {
  const normalizedEvent = event || {};
  if (!normalizedEvent.is_free) return sold_ticket_count(normalizedEvent);

  return normalizedEvent.rsvp_list?.filter(
    rsvp => String(rsvp?.rsvp_status?.name || "").toLowerCase() === "confirmed"
  ).length || 0;
};

function capacity_value(event) {
  const rsvpCapacity = Number(event?.rsvp_cap?.rsvp_max_capacity ?? 0);
  if (rsvpCapacity > 0) return rsvpCapacity;

  const ticketCapacity = Array.isArray(event?.event_tickets)
    ? event.event_tickets.reduce((sum, ticket) => sum + Number(ticket.quantity ?? 0), 0)
    : 0;

  return ticketCapacity > 0 ? ticketCapacity : 0;
};

function capacity_percent(total, capacity) {
  if (!capacity) return 0;
  const percent = (total / capacity) * 100;
  return Math.max(0, Math.min(100, Math.round(percent)));
};

function capacity_text(total, capacity) {
  if (!capacity) return "Capacity Maximum";
  return `${total} of ${capacity} capacity`;
};

function event_date_line(event) {
  if (!event) return "";
  const startDate = event_utility_helpers.format_date(event.start_date);
  const endDate = event_utility_helpers.format_date(event.end_date);
  const startTime = event_utility_helpers.format_time(event.start_time);
  const endTime = event_utility_helpers.format_time(event.end_time);
  if (startDate === endDate) {
    return `${startDate} \u00b7 ${startTime || ""}${startTime && endTime ? " - " : ""}${endTime || ""}`.trim();
  }
  return `${startDate} ${startTime || ""} - ${endDate} ${endTime || ""}`.trim();
};

function ticket_breakdown(event) {
  const items = [];
  if (event?.is_free) {
    items.push({
      label: "Free RSVPs",
      value: Number(
        event?.rsvp_count
        ?? event?.rsvp_list?.filter(
          rsvp => rsvp.rsvp_status?.name === "Confirmed"
        ).length
        ?? 0
      ),
      sublabel: "confirmed",
    });
  }
  if (Array.isArray(event?.event_tickets)) {
    event.event_tickets.slice(0, 3).forEach((ticket) => {
      const sold = ticket?.sold_instances !== undefined && ticket?.sold_instances !== null
        ? Number(ticket.sold_instances || 0)
        : Math.max(
          0,
          Number(ticket?.quantity || 0) - Number(ticket?.available_instances || 0)
        );
      items.push({
        label: ticket?.ticket?.name || "Ticket",
        value: event?.is_free ? Number(ticket?.quantity ?? 0) : sold,
        sublabel: event?.is_free ? "available" : "sold",
      });
    });
  }
  return items;
};

function can_edit(token_exists, event, user_details, user_id) {
  if (!token_exists || !event) return false;
  return user_details?.user_role === "admin"
    || String(event?.user?.id) === String(user_id);
};

function show_ticket_rsvp_card(
  event,
  user_details,
  user_id,
) {
  if (!event) return false;
  return user_details?.user_role !== "admin"
    || String(event?.user?.id) !== String(user_id);
};

function is_event_attendee(
  token_exists,
  event,
  user_details,
  user_id,
  event_ticket_attendees = [],
) {
  const userId = String(user_id || "");
  if ([!token_exists, !event, !user_id].some(Boolean)) return false;

  if ([
    user_details?.user_role === "admin",
    String(event?.user?.id) === userId,
  ].some(Boolean)) {
    return true;
  }

  if ([event?.is_waitlisted, event?.is_rsvped].includes(true)) {
    return !event?.is_waitlisted;
  }

  return [event?.rsvp_list, event_ticket_attendees].some(
    (list) =>
      Array.isArray(list)
      && list.some((entry) =>
        !["cancelled", "waitlisted"].includes(
          String(entry?.rsvp_status?.name || "").toLowerCase()
        )
        && [entry?.user?.id, entry?.user_id]
          .map((id) => String(id || ""))
          .includes(userId)
      )
  );
};

function can_view_event_content(
  is_public,
  token_exists,
  event,
  user_details,
  user_id,
  event_ticket_attendees = [],
) {
  if (!event) return false;
  if (is_public !== false) return true;
  return is_event_attendee(
    token_exists,
    event,
    user_details,
    user_id,
    event_ticket_attendees,
  );
}

function can_view_gallery(
  token_exists,
  event,
  user_details,
  user_id,
  event_ticket_attendees = [],
) {
  return can_view_event_content(
    event?.public_gallery,
    token_exists,
    event,
    user_details,
    user_id,
    event_ticket_attendees,
  );
}

function can_view_materials(
  token_exists,
  event,
  user_details,
  user_id,
  event_ticket_attendees = [],
) {
  return can_view_event_content(
    event?.public_slides,
    token_exists,
    event,
    user_details,
    user_id,
    event_ticket_attendees,
  );
}

function gallery_preview(event, canViewGallery = true) {
  if (!event || !canViewGallery) return [];
  return event_utility_helpers.get_event_media_files(event).slice(0, 4);
};

function has_more_gallery(event, canViewGallery = true) {
  if (!event || !canViewGallery) return false;
  return event_utility_helpers.get_event_media_files(event).length > 4;
};

function materials_list(event, canViewMaterials = true) {
  if (!event || !canViewMaterials) return [];
  return event_utility_helpers.get_event_resource_files(event);
};

function materials_preview(list) {
  return list.slice(0, 4);
}

function has_more_materials(list) {
  return list.length > 4;
}

function materials_count(list) {
  return list.length;
}

function description_text_length(event) {
  if (!event?.description) return 0;
  return event.description.replace(/<[^>]*>/g, "").trim().length;
};

function has_long_description(event) {
  if (!event?.description) return false;
  const plainText = event.description.replace(/<[^>]*>/g, "").trim();
  return plainText.split(/\s+/).filter(Boolean).length > 100;
};

function plain_description(event) {
  if (!event?.description) return "";
  return event.description.replace(/<[^>]*>/g, "").trim();
};

function truncated_description(plainText) {
  return event_utility_helpers.truncate_description(plainText, 100);
}

function map_embed_url(event) {
  if (event?.event_type?.name === "online") return "";
  const location = String(event?.location || "").trim();
  if (!location) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(location)}&z=15&output=embed`;
};

function selected_ticket(eventTickets, selectedTicketId) {
  if (!selectedTicketId) return null;
  return eventTickets.find(
    ticket => String(ticket.id) === String(selectedTicketId)
  ) || null;
};

function selected_tickets_total(eventTickets) {
  return eventTickets.reduce((sum, ticket) => {
    const quantity = Number(ticket?.quantitySelected || 0);
    const price = Number(ticket?.price || 0);
    return sum + quantity * price;
  }, 0);
}

const view_event_method_helpers = {
  total_registrations,
  capacity_value,
  capacity_percent,
  capacity_text,
  event_date_line,
  ticket_breakdown,
  can_edit,
  show_ticket_rsvp_card,
  is_event_attendee,
  can_view_gallery,
  can_view_materials,
  gallery_preview,
  has_more_gallery,
  materials_list,
  materials_preview,
  has_more_materials,
  materials_count,
  description_text_length,
  has_long_description,
  plain_description,
  truncated_description,
  map_embed_url,
  selected_ticket,
  selected_tickets_total,
};

export default view_event_method_helpers;
